import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowLeft,
  Settings as SettingsIcon,
  User,
  Bell,
  Shield,
  Trash2,
  Download,
  Upload,
  Moon,
  Sun,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    notifications: true,
    darkMode: true,
    autoSync: false,
    dataRetention: 365,
    reminderTime: '20:00',
    weeklyReport: true,
    shareData: false,
  });

  const handleSettingChange = (setting: string, value: any) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
  };

  const exportData = () => {
    const data = localStorage.getItem('peakstate-survey-data');
    if (data) {
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `peakstate-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const importData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          localStorage.setItem('peakstate-survey-data', JSON.stringify(data));
          window.location.reload();
        } catch (error) {
          alert('Invalid file format');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to delete all your data? This action cannot be undone.')) {
      localStorage.removeItem('peakstate-survey-data');
      localStorage.removeItem('peakstate-health-metrics');
      window.location.reload();
    }
  };

  const resetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      setSettings({
        notifications: true,
        darkMode: true,
        autoSync: false,
        dataRetention: 365,
        reminderTime: '20:00',
        weeklyReport: true,
        shareData: false,
      });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)' }}>
      <AppBar position="static" sx={{ background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')}>
            <ArrowLeft />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <SettingsIcon size={32} color="#00ff88" />
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 700 }}>
              PeakState Settings
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Grid container spacing={3}>
            {/* General Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
                    General Settings
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Bell color="#00ff88" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Daily Reminders"
                        secondary="Get notified to complete your daily survey"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications}
                          onChange={(e) => handleSettingChange('notifications', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#00ff88',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#00ff88',
                            },
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>

                    <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />

                    <ListItem>
                      <ListItemIcon>
                        {settings.darkMode ? <Moon color="#00ff88" /> : <Sun color="#00ff88" />}
                      </ListItemIcon>
                      <ListItemText
                        primary="Dark Mode"
                        secondary="Use dark theme for better eye comfort"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.darkMode}
                          onChange={(e) => handleSettingChange('darkMode', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#00ff88',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#00ff88',
                            },
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>

                    <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />

                    <ListItem>
                      <ListItemIcon>
                        <User color="#00ff88" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Weekly Reports"
                        secondary="Receive weekly performance summaries"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.weeklyReport}
                          onChange={(e) => handleSettingChange('weeklyReport', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#00ff88',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#00ff88',
                            },
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Data Management */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
                    Data Management
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={exportData}
                      sx={{
                        borderColor: '#00ff88',
                        color: '#00ff88',
                        '&:hover': {
                          borderColor: '#00ff88',
                          backgroundColor: 'rgba(0, 255, 136, 0.1)',
                        },
                      }}
                    >
                      Export Data
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      component="label"
                      sx={{
                        borderColor: '#00ff88',
                        color: '#00ff88',
                        '&:hover': {
                          borderColor: '#00ff88',
                          backgroundColor: 'rgba(0, 255, 136, 0.1)',
                        },
                      }}
                    >
                      Import Data
                      <input
                        type="file"
                        accept=".json"
                        hidden
                        onChange={importData}
                      />
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<Trash2 />}
                      onClick={clearAllData}
                      sx={{
                        borderColor: '#ff6b6b',
                        color: '#ff6b6b',
                        '&:hover': {
                          borderColor: '#ff6b6b',
                          backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        },
                      }}
                    >
                      Clear All Data
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Privacy & Security */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
                    Privacy & Security
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Shield color="#00ff88" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Data Sharing"
                        secondary="Allow anonymous data sharing for research purposes"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.shareData}
                          onChange={(e) => handleSettingChange('shareData', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#00ff88',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#00ff88',
                            },
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Your data is stored locally on your device and is never transmitted to external servers unless you explicitly enable data sharing.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Advanced Settings */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
                    Advanced Settings
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="Reminder Time"
                      type="time"
                      value={settings.reminderTime}
                      onChange={(e) => handleSettingChange('reminderTime', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': {
                            borderColor: '#00ff88',
                          },
                          '&:hover fieldset': {
                            borderColor: '#00ff88',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00ff88',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#00ff88',
                        },
                      }}
                    />
                    
                    <TextField
                      label="Data Retention (days)"
                      type="number"
                      value={settings.dataRetention}
                      onChange={(e) => handleSettingChange('dataRetention', parseInt(e.target.value))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': {
                            borderColor: '#00ff88',
                          },
                          '&:hover fieldset': {
                            borderColor: '#00ff88',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00ff88',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#00ff88',
                        },
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={resetSettings}
                      sx={{
                        borderColor: '#ffd700',
                        color: '#ffd700',
                        '&:hover': {
                          borderColor: '#ffd700',
                          backgroundColor: 'rgba(255, 215, 0, 0.1)',
                        },
                      }}
                    >
                      Reset to Default
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* App Information */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
                    App Information
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Version: 1.0.0
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Build: HackYeah 2025
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated: {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    BioHack Pro is designed to help you optimize your health and performance through data-driven insights and personalized recommendations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Settings;
